import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Accordion, Box, Text} from '@chakra-ui/react3'
import Copy from '../../components/cms/copy'
import CTA from '../../components/cms/cta'
import Image from '../../components/cms/image'
import Video from '../../components/cms/video'
import {
    hasValidString,
    hasValidAsset,
    hasValidVideoContent,
    hasValidImageSrc
} from '../../utils/utils'
import {AddIcon, RemoveIcon} from '../../components/icons'
import ButtonGroup from '../../components/cms/button-group'
import VideoControl from '../../components/cms/video-control'
import useVideoControl from '../../hooks/useVideoControl'
import {useStyles} from './context'

const AccordionItem = ({alignment, asset, cta, copy, headline, value, widths, moduleType}) => {
    const styles = useStyles()
    const videoRef = React.useRef(null)
    const [isVideoPlaying, onVideoToggle] = useVideoControl(asset?.autoplay, videoRef)

    const {hasAsset, hasVideo, hasImage, hasCta, hasCopy} = useMemo(
        () => ({
            hasAsset: hasValidAsset(asset),
            hasVideo: hasValidVideoContent(asset),
            hasImage: hasValidImageSrc(asset),
            hasCta: hasValidString(cta?.label),
            hasCopy: hasValidString(copy?.text)
        }),
        [asset, cta, copy]
    )

    return (
        <Accordion.Item value={value} css={styles.item}>
            <Accordion.ItemTrigger css={styles.trigger}>
                <Text as="span" css={styles.itemHeadline}>
                    {headline}
                </Text>
                <Accordion.ItemIndicator css={styles.indicator} _open={{display: 'none'}}>
                    <AddIcon />
                </Accordion.ItemIndicator>
                <Accordion.ItemIndicator css={styles.indicator} _closed={{display: 'none'}}>
                    <RemoveIcon />
                </Accordion.ItemIndicator>
            </Accordion.ItemTrigger>

            <Accordion.ItemContent css={styles.content}>
                <Box css={styles.contentInner}>
                    {/* Text content (copy and/or CTA) */}
                    {(hasCopy || hasCta) && (
                        <Box css={styles.textContent}>
                            {hasCopy && <Copy {...copy} alignment={alignment} />}
                            {hasCta && (
                                <ButtonGroup>
                                    <CTA
                                        {...cta}
                                        css={styles.cta}
                                        trackingContext={{
                                            moduleName: moduleType,
                                            eventLabel: 'primaryCta'
                                        }}
                                    />
                                </ButtonGroup>
                            )}
                        </Box>
                    )}

                    {/* Asset (image or video) */}
                    {hasAsset && (
                        <Box css={styles.assetWrapper}>
                            {hasVideo && (
                                <>
                                    <Video {...asset} ref={videoRef} widths={widths} />
                                    <Box css={styles.videoDarkenLayer} />
                                    <VideoControl
                                        {...styles.videoControl}
                                        variant="mediaFilled"
                                        isPlaying={isVideoPlaying}
                                        onToggle={onVideoToggle}
                                    />
                                </>
                            )}
                            {hasImage && <Image {...asset} widths={widths} objectFit="contain" />}
                        </Box>
                    )}
                </Box>
            </Accordion.ItemContent>
        </Accordion.Item>
    )
}

AccordionItem.propTypes = {
    alignment: PropTypes.string,
    asset: PropTypes.object,
    cta: PropTypes.object,
    copy: PropTypes.object,
    headline: PropTypes.string,
    value: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    moduleType: PropTypes.string
}

export default AccordionItem
